import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { useTranslations } from "next-intl";

import { InstructionState } from "@generated/types";

interface InstructionStatusBadgeProps {
  status: InstructionState;
  className?: string;
  size?: "xs" | "sm";
  mobileSize?: "xs" | "sm";
}

export default function InstructionStatusBadge({
  status,
  className,
  size = "sm",
  mobileSize = "xs",
}: InstructionStatusBadgeProps) {
  const t = useTranslations();
  const isMobile = useIsMobile();

  const getStatusStyles = () => {
    switch (status) {
      case InstructionState.Unconfirmed:
        return "bg-orange-100 text-orange-800 border-orange-300";
      case InstructionState.Confirmed:
      case InstructionState.Accepted:
        return "bg-green-100 text-green-800 border-green-200";
      case InstructionState.Cancelled:
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-brand-primary/10 text-gray-800 border-line";
    }
  };

  const getStatusText = () => {
    switch (status) {
      case InstructionState.Unconfirmed:
        return t("instructionStatus.unconfirmed");
      case InstructionState.Confirmed:
        return t("instructionStatus.confirmed");
      case InstructionState.Accepted:
        return t("instructionStatus.accepted");
      case InstructionState.Cancelled:
        return isMobile
          ? t("instructionStatus.cancelledMobile")
          : t("instructionStatus.cancelled");
      default:
        return status;
    }
  };

  const statusText = getStatusText();
  const isProcessing = status === InstructionState.Unconfirmed;

  return (
    statusText && (
      <div>
        <p
          className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
            size === "xs" && "text-xs",
            size === "sm" && "text-sm",
            isMobile && mobileSize === "xs" && "text-xs",
            isMobile && mobileSize === "sm" && "text-sm",
            getStatusStyles(),
            isProcessing && "animate-pulse",
            className
          )}
        >
          {statusText}
        </p>
      </div>
    )
  );
}
